import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, Observable, Subscription } from 'rxjs';
import { PaginatedResponse } from 'src/app/interfaces/core/core.entity';

export type PaginatedFilters = {
	search?: {
		value: string;
		fields: string[];
	};
	filters: Record<string, string>;
};

export type SortDirection = 'ASC' | 'DESC';

export type PaginatedQuery<T> = (
	limit: number,
	offset: number,
	filters: PaginatedFilters,
	sortBy?: string,
	sortDirection?: SortDirection
) => Observable<PaginatedResponse<T>>;

export type ReloadQuery<T> = () => Observable<T[]>;

@Component({
	template: ''
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class TablePagination<T> implements AfterViewInit {
	// Los view children usan setter porque el paginador y el ordenamiento
	// pueden estar dentro de un `*ngIf` y aparecer después de que la data cargue.
	@ViewChild(MatPaginator)
	set paginator(paginator: MatPaginator | undefined) {
		this._paginator = paginator;
		if (!this.viewInitialized) {
			return;
		}
		if (this.paginatedQuery) {
			this.bindPageEvents();
			this.syncPaginator();
		} else {
			this.dataSource.paginator = paginator ?? null;
		}
	}

	get paginator(): MatPaginator {
		return this._paginator as MatPaginator;
	}

	@ViewChild(MatSort)
	set sort(sort: MatSort | undefined) {
		this._sort = sort;
		if (!this.viewInitialized) {
			return;
		}
		if (this.paginatedQuery) {
			this.bindSortEvents();
		} else {
			this.dataSource.sort = sort ?? null;
		}
	}

	get sort(): MatSort {
		return this._sort as MatSort;
	}

	@ViewChild('focus') tableInput!: ElementRef;
	selection = new SelectionModel<T>(true, []);

	afterViewInitCustom?: Function;
	dataSource: MatTableDataSource<T>;
	pendingSelectedRows: string[] = [];
	limit: number = 10;
	offset: number = 0;
	total: number = 0;
	hasMore: boolean = false;
	private fetching: boolean = false;
	private paginatedQuery?: PaginatedQuery<T>;
	private reloadQuery?: ReloadQuery<T>;
	private filterColumns: string[] = [];
	private searchValue: string = '';
	private columnFilters: Record<string, string> = {};
	private filterDebounceTimer?: ReturnType<typeof setTimeout>;
	private pageSubscription?: Subscription;
	private sortSubscription?: Subscription;
	private requestSubscription?: Subscription;
	private viewInitialized = false;
	private sortBy?: string;
	private sortDirection?: SortDirection;
	private _paginator?: MatPaginator;
	private _sort?: MatSort;

	get isFetching(): boolean {
		return this.fetching;
	}

	/** Whether there are filters applied to the table (search, column or local filter). */
	get hasFilters(): boolean {
		if (this.paginatedQuery) {
			return this.searchValue.trim().length > 0 || Object.keys(this.columnFilters).length > 0;
		}

		return (this.dataSource.filter ?? '').trim().length > 0;
	}

	constructor(@Inject('dataForTable') dataForTable: T[], afterViewInitCustom?: Function) {
		this.dataSource = new MatTableDataSource(dataForTable);
		this.afterViewInitCustom = afterViewInitCustom;
	}

	ngAfterViewInit() {
		this.viewInitialized = true;
		if (this.paginatedQuery) {
			this.initializePaginatedQuery();
		} else {
			this.dataSource.paginator = this._paginator ?? null;
			this.dataSource.sort = this._sort ?? null;
		}

		if (this.afterViewInitCustom) {
			this.afterViewInitCustom();
		}
	}

	setPaginatedQuery(query: PaginatedQuery<T>, filterColumns: string[] = []): void {
		this.paginatedQuery = query;
		this.filterColumns = filterColumns;
		if (this.viewInitialized) {
			this.initializePaginatedQuery();
		}
	}

	setReloadQuery(query: ReloadQuery<T>): void {
		this.reloadQuery = query;
	}

	reloadData(source?: ReloadQuery<T> | T[]): void {
		if (this.paginatedQuery) {
			if (!this.viewInitialized) {
				return;
			}
			this.clearFilterDebounce();
			this.loadPage(this.limit, this.offset);
			return;
		}

		const reloadQuery = source ?? this.reloadQuery;

		if (!reloadQuery) {
			return;
		}

		if (Array.isArray(reloadQuery)) {
			this.total = reloadQuery.length;
			this.hasMore = false;
			this.setData(reloadQuery);
			return;
		}

		this.requestSubscription?.unsubscribe();
		this.fetching = true;
		this.requestSubscription = reloadQuery().pipe(
			finalize(() => this.fetching = false)
		).subscribe({
			next: data => {
				this.total = data.length;
				this.hasMore = false;
				this.setData(data);
			}
		});
	}

	private initializePaginatedQuery(): void {
		this.dataSource.paginator = null;
		this.dataSource.sort = null;
		this.bindPageEvents();
		this.bindSortEvents();
		Promise.resolve().then(() => {
			const pageSize = this._paginator?.pageSize || this.limit;
			const pageIndex = this._paginator?.pageIndex ?? 0;
			this.loadPage(pageSize, pageIndex * pageSize);
		});
	}

	private bindPageEvents(): void {
		this.pageSubscription?.unsubscribe();
		this.pageSubscription = undefined;
		if (!this._paginator) {
			return;
		}
		this.pageSubscription = this._paginator.page.subscribe(event => {
			this.clearFilterDebounce();
			this.loadPage(event.pageSize, event.pageIndex * event.pageSize);
		});
	}

	private bindSortEvents(): void {
		this.sortSubscription?.unsubscribe();
		this.sortSubscription = undefined;
		if (!this._sort) {
			return;
		}
		this.sortSubscription = this._sort.sortChange.subscribe(event => {
			this.clearFilterDebounce();
			this.sortBy = event.direction ? event.active : undefined;
			this.sortDirection = event.direction === 'asc' ? 'ASC' : event.direction === 'desc' ? 'DESC' : undefined;
			if (this._paginator) {
				this._paginator.pageIndex = 0;
			}
			this.loadPage(this.limit, 0);
		});
	}

	private syncPaginator(): void {
		if (!this._paginator) {
			return;
		}
		this._paginator.pageIndex = this.limit > 0 ? Math.floor(this.offset / this.limit) : 0;
		this._paginator.length = this.hasMore
			? this.total
			: Math.min(this.total, this.offset + this.dataSource.data.length);
	}

	private loadPage(limit: number, offset: number): void {
		if (!this.paginatedQuery) {
			return;
		}

		this.requestSubscription?.unsubscribe();
		this.fetching = true;
		const filters: PaginatedFilters = {
			filters: { ...this.columnFilters }
		};
		if (this.searchValue) {
			filters.search = {
				value: this.searchValue,
				fields: this.filterColumns
			};
		}

		this.requestSubscription = this.paginatedQuery(limit, offset, filters, this.sortBy, this.sortDirection).pipe(
			finalize(() => this.fetching = false)
		).subscribe({
			next: response => {
				if (response.data.length === 0 && response.total > 0 && offset > 0) {
					const lastPageOffset = Math.floor((response.total - 1) / limit) * limit;
					if (lastPageOffset < offset) {
						Promise.resolve().then(() => this.loadPage(limit, lastPageOffset));
						return;
					}
				}

				this.limit = limit;
				this.offset = response.offset;
				this.total = response.total;
				this.hasMore = response.hasMore;
				this.setData(response.data);
				this.syncPaginator();
			},
			error: () => {
				if (this._paginator) {
					this._paginator.pageIndex = Math.floor(this.offset / limit);
				}
			}
		});
	}

	applyFilter(event: unknown, columnName?: string) {
		let filterValue = '';
		if (typeof event === 'object' && event !== null && 'target' in event) {
			filterValue = ((event as { target: HTMLInputElement }).target).value;
		} else if (typeof event === 'object' && event !== null && 'value' in event) {
			const value = (event as { value: unknown }).value;
			filterValue = typeof value === 'string' ? value : '';
		} else if (typeof event === 'string') {
			filterValue = event;
		}

		if (this.paginatedQuery && this.filterColumns.length > 0) {
			const normalizedValue = filterValue.trim();
			if (columnName) {
				if (normalizedValue) {
					this.columnFilters[columnName] = normalizedValue;
				} else {
					delete this.columnFilters[columnName];
				}
			} else {
				this.searchValue = normalizedValue;
			}
			if (this._paginator) {
				this._paginator.pageIndex = 0;
			}
			this.clearFilterDebounce();
			this.filterDebounceTimer = setTimeout(() => {
				this.filterDebounceTimer = undefined;
				this.loadPage(this.limit, 0);
			}, 350);
			return;
		}

		this.dataSource.filter = filterValue;
	}

	private clearFilterDebounce(): void {
		if (this.filterDebounceTimer !== undefined) {
			clearTimeout(this.filterDebounceTimer);
			this.filterDebounceTimer = undefined;
		}
	}

	private setSelectionFromObjectRows(selectedRows: string[]) {
		this.selection.select(...this.dataSource.data.
			filter(row => typeof row === 'object' && row !== null && 'id' in row)
			.filter(row => selectedRows.some(selectedRow => selectedRow === (row as Record<string, unknown>)['id'])));

		this.pendingSelectedRows = [];
	}

	setData(dataTable: T[]) {
		this.dataSource = new MatTableDataSource<T>([...dataTable]);
		if (!this.paginatedQuery) {
			this.dataSource.paginator = this._paginator ?? null;
			this.dataSource.sort = this._sort ?? null;
		}

		if (this.pendingSelectedRows.length > 0) {
			this.setSelectionFromObjectRows(this.pendingSelectedRows);
		}
	}

	scrollUp(): void {
		this.tableInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: "end" });
	}

	/** Whether all filtered rows are selected. */
	isAllSelected() {
		return this.dataSource.filteredData.length > 0 &&
			this.dataSource.filteredData.every(row => this.selection.isSelected(row));
	}

	/** Selects all filtered rows if not all selected; otherwise deselects only filtered rows. */
	toggleAllRows() {
		if (this.isAllSelected()) {
			this.selection.deselect(...this.dataSource.filteredData);
			return;
		}

		this.selection.select(...this.dataSource.filteredData);
	}

	/** The label for the checkbox on the passed row */
	checkboxLabel(row?: T): string {
		if (!row) {
			return `${this.isAllSelected() ? 'Deseleccionar' : 'Seleccionar'} todo`;
		}
		return `${this.selection.isSelected(row) ? 'Deseleccionar' : 'Seleccionar'} fila ${this.dataSource.data.indexOf(row) + 1}`;
	}

	/** Get selected rows */
	getSelectedRows(): T[] {
		return this.selection.selected;
	}

	/** Set selected rows */
	setSelectedRows(rows: string[]) {
		// If the data source is empty, store the selected rows in pendingSelectedRows to be set later when data is available
		if (this.dataSource.data.length === 0) {
			this.pendingSelectedRows = rows;
		} else {
			this.setSelectionFromObjectRows(rows);
		}
	}
}
